import {DotLottiePlayer, type DotLottieCommonPlayer} from '@dotlottie/react-player'
import '@dotlottie/react-player/dist/index.css'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useInView} from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {
  ButtonDocument,
  HeroV2SliceBentoGridReskinItem,
  HeroV2SliceBentoGridReskinPrimary,
  HeroV2SliceBentoGridReskinPrimaryBentoGridItem,
} from 'prismicio-types'
import {useEffect, useRef} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {DARK_BACKGROUND_COLORS} from 'src/common/constants'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {Icon} from 'src/common/Icon'
import {Tag} from 'src/common/Tag'
import {RatingsSection} from './RatingsSection'

type BentoGridReskinProps = {
  slice: {
    primary: HeroV2SliceBentoGridReskinPrimary
    items: HeroV2SliceBentoGridReskinItem[]
    slice_type: string
    variation: string
  }
  pageUid: string
  componentIndex: number
  buttonsData?: ButtonDocument[]
}

export const BentoGridHeroV2Reskin = ({slice, pageUid, componentIndex, buttonsData}: BentoGridReskinProps) => {
  const {linkResolver} = useLinkResolver()
  const sanitizedHeading = sanitizeHtml(slice.primary.heading!)
  const isDarkBackground = DARK_BACKGROUND_COLORS.includes(slice.primary.background_color || '')
  const hasVideo = isFilled.linkToMedia(slice.primary.video)
  const hasLottie = isFilled.link(slice.primary.lottie_animation)

  const lottieRef = useRef<DotLottieCommonPlayer>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, {amount: 0.5, once: true})

  // Trigger Lottie animation play when container is in view
  useEffect(() => {
    if (isInView && lottieRef.current) {
      lottieRef.current.play()
    }
  }, [isInView])

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.primary_button,
    secondaryButton: slice.primary.secondary_button,
  })

  return (
    <Container
      backgroundColor={slice.primary.background_color!}
      className="flex flex-col !gap-0 !pb-12 !pt-[122px] sm:!gap-[60px] md:!pt-[137px] xl:!gap-0"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      extraPaddingTop
    >
      <div className="mx-auto grid w-full max-w-[1200px] scroll-mt-16 grid-cols-4 gap-4 gap-y-12 px-5 md:grid-cols-12 md:gap-x-6 md:px-10 lg:px-5 xl:px-0">
        <div
          className={cn('col-span-full flex flex-col gap-y-18 md:grid md:grid-cols-subgrid md:gap-y-0', {
            'gap-y-6': slice.primary.text_left_align_on_mobile,
          })}
        >
          <div
            className="max-md:grid-base grid items-center !gap-y-6 md:col-span-5 md:grid-cols-subgrid md:items-start md:self-center"
            data-aos="fade-up"
            data-aos-offset="0"
          >
            <div className="col-span-4 grid grid-cols-subgrid gap-6 text-center md:col-span-full md:grid-cols-subgrid md:text-start">
              {isFilled.keyText(slice.primary.neon_green_header) && (
                <p
                  className={cn('text-button-s col-span-full uppercase text-green-neon', {
                    'max-md:text-start': slice.primary.text_left_align_on_mobile,
                  })}
                >
                  {slice.primary.neon_green_header}
                </p>
              )}
              <h1
                className={cn('text-h2 col-span-full lg:text-[48px] lg:leading-14', {
                  'text-moss-white': isDarkBackground,
                  'text-black': !isDarkBackground,
                  'max-md:text-start': slice.primary.text_left_align_on_mobile,
                })}
                dangerouslySetInnerHTML={{__html: sanitizedHeading}}
              />
              <p
                className={cn('text-tg-body-m md:text-tg-body-l col-span-4', {
                  'text-moss-white': isDarkBackground,
                  'text-grey-550': !isDarkBackground,
                  'max-md:text-start': slice.primary.text_left_align_on_mobile,
                })}
              >
                {slice.primary.subtext}
              </p>
              {isFilled.group(slice.primary.benefits) && (
                <div className="col-span-full flex flex-col gap-2">
                  {slice.primary.benefits.map((item, index) => (
                    <div className="flex items-center gap-3" key={`benefit-${index}`}>
                      <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-moss-white/30">
                        <Icon name="check-mark" className="size-2 text-moss-white" />
                      </div>
                      <p
                        className={cn('text-body-m', {
                          'text-moss-white': isDarkBackground,
                          'text-grey-550': !isDarkBackground,
                        })}
                      >
                        {item.benefit_label}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              className={cn('col-span-full flex flex-wrap justify-center gap-2 md:justify-start md:gap-4 md:pt-2', {
                'max-md:justify-start': slice.primary.text_left_align_on_mobile,
              })}
            >
              {primaryButtonData && (
                <ButtonV2
                  variant="primary"
                  href={linkResolver(primaryButtonData.link, 'primary-button-hero')}
                  newTrackingProps={{
                    interaction: {
                      element: primaryButtonData.marketing_id || 'primary-button-hero',
                      elementText: primaryButtonData.label || '',
                      location: `component-${componentIndex}-hero`,
                    },
                    pageType: pageUid,
                  }}
                  trackingData={{
                    location: `component-${componentIndex}-hero`,
                    dataType: 'primary-button',
                    pageName: pageUid,
                    mktgId: primaryButtonData.marketing_id || '',
                    label: primaryButtonData.label!,
                  }}
                  id={`component-${componentIndex}-hero-1`}
                  className="uppercase"
                >
                  {primaryButtonData.label}
                </ButtonV2>
              )}
              {isFilled.keyText(slice.primary.scroll_into_view_button_text) &&
                isFilled.keyText(slice.primary.scroll_into_view_button_id) && (
                  <ButtonV2
                    variant="primary"
                    onClick={() => {
                      document
                        .getElementById(slice.primary.scroll_into_view_button_id!)
                        ?.scrollIntoView({behavior: 'smooth'})
                    }}
                    newTrackingProps={{
                      interaction: {
                        element: 'scroll-into-view-button-hero',
                        elementText: slice.primary.scroll_into_view_button_text || '',
                        location: `component-${componentIndex}-hero`,
                      },
                      pageType: pageUid,
                    }}
                    trackingData={{
                      location: `component-${componentIndex}-hero`,
                      dataType: 'scroll-into-view-button',
                      pageName: pageUid,
                      mktgId: slice.primary.scroll_into_view_button_id,
                      label: slice.primary.scroll_into_view_button_text!,
                    }}
                    id={`component-${componentIndex}-hero-scroll`}
                    className="uppercase"
                  >
                    {slice.primary.scroll_into_view_button_text}
                  </ButtonV2>
                )}
              {secondaryButtonData && (
                <ButtonV2
                  href={linkResolver(secondaryButtonData.link, 'secondary-button-hero')}
                  variant="secondary"
                  trackingData={{
                    location: `component-${componentIndex}-hero`,
                    dataType: 'secondary-button',
                    pageName: pageUid,
                    mktgId: secondaryButtonData.marketing_id || '',
                    label: secondaryButtonData.label!,
                  }}
                  id={`component-${componentIndex}-hero-2`}
                  className="uppercase text-black"
                  hideOnMobile={secondaryButtonData.hide_on_mobile}
                >
                  {secondaryButtonData?.label}
                </ButtonV2>
              )}
            </div>
            {isFilled.richText(slice.primary.rich_subtext) && (
              <div
                className={cn('text-body-s col-span-full -mt-4 text-center md:text-start', {
                  'text-moss-white/70': isDarkBackground,
                  'text-grey-300': !isDarkBackground,
                  'max-md:text-start': slice.primary.text_left_align_on_mobile,
                })}
              >
                <PrismicRichText
                  components={{
                    paragraph: ({children}) => <p>{children}</p>,
                    hyperlink: ({children, node}) => (
                      <Link
                        className={cn('underline transition-colors', {
                          'text-inherit hover:text-moss-white': isDarkBackground && slice.primary.stealth_links,
                          'text-inherit hover:text-grey-550': !isDarkBackground && slice.primary.stealth_links,
                          'text-7290f3 hover:text-7290f3/80': !slice.primary.stealth_links,
                        })}
                        href={node.data.url || linkResolver(node.data)}
                        {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                        {...(node.data.link_type === 'Web' &&
                          node.data.target === '_blank' && {rel: 'noopener noreferrer'})}
                      >
                        {children}
                      </Link>
                    ),
                  }}
                  field={slice.primary.rich_subtext}
                />
              </div>
            )}
            {isFilled.keyText(slice.primary.tag) && <Tag label={slice.primary.tag} />}
            {isFilled.group(slice.primary.ratings) && (
              <div
                className={cn('col-span-full flex gap-3', {
                  'row-start-1 justify-center md:justify-start': slice.primary.ratings_on_top,
                })}
              >
                <RatingsSection
                  ratings={slice.primary.ratings}
                  type={slice.primary.ratings_type}
                  centered={false}
                  iconContainerColor="Moss Black"
                />
              </div>
            )}
            {isFilled.keyText(slice.primary.disclaimer) && (
              <p
                className={cn('text-body-s col-span-full', {
                  'text-moss-white': isDarkBackground,
                  'text-grey-300': !isDarkBackground,
                })}
              >
                {slice.primary.disclaimer}
              </p>
            )}
          </div>
          <div
            ref={containerRef}
            className={cn(
              'max-md:grid-base grid gap-y-4 self-center md:col-span-6 md:col-start-7 md:grid-cols-subgrid md:gap-y-6',
              {
                'aspect-square': !slice.primary.disable_square_image,
              },
            )}
          >
            {hasVideo && (
              <div
                className="col-span-full w-full self-center overflow-hidden rounded-lg"
                data-aos="fade-up"
                data-aos-offset="0"
              >
                <video
                  src={(slice.primary.video as {url: string}).url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="size-full object-cover"
                />
              </div>
            )}
            {!hasVideo && hasLottie && (
              <div
                className="col-span-full w-full max-w-[480px] self-center overflow-hidden rounded-lg max-lg:mx-auto lg:ml-4"
                data-aos="fade-up"
                data-aos-offset="0"
              >
                <DotLottiePlayer
                  autoplay
                  loop
                  src={(slice.primary.lottie_animation as {url: string}).url}
                  ref={lottieRef}
                  className="aspect-[480/460] w-full scale-[1.1]"
                />
              </div>
            )}
            {!hasVideo &&
              !hasLottie &&
              slice.primary.bento_grid.map((item, index) => {
                if (isFilled.linkToMedia(item.asset) || isFilled.image(item.image)) {
                  return (
                    <div
                      key={`bento-grid-item-${index}`}
                      className={cn('size-full self-center overflow-hidden rounded-lg', {
                        'col-span-2 md:col-span-3': index !== slice.primary.bento_grid.length - 1,
                        'col-span-full aspect-[2/1]':
                          index === slice.primary.bento_grid.length - 1 && slice.primary.bento_grid.length > 2,
                        'col-span-full': slice.primary.bento_grid.length === 1,
                        'lg:max-h-[282px]': slice.primary.bento_grid.length === 3,
                      })}
                      data-aos="fade-up"
                      data-aos-offset="0"
                    >
                      <MediaComponent assetField={item} />
                    </div>
                  )
                } else {
                  return null
                }
              })}
          </div>
        </div>
        {!!slice.items.length && (
          <div className="col-span-full flex flex-col gap-6 md:flex-row md:gap-20">
            {slice.items.map((item, index) => (
              <div className="flex w-full flex-col items-center gap-3 md:items-start" key={item.title! + index}>
                <div className="relative flex size-8 items-center justify-center rounded-md bg-moss-white">
                  <PrismicNextImage field={item.icon} className="size-4 object-cover" />
                  <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]" />
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="text-h5 text-center leading-7 text-black md:text-start">{item.title}</h3>
                  <p className="text-center text-[15px] leading-[22px] text-grey-450 md:text-start">{item.subtext}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mx-auto grid w-full max-w-[1200px] scroll-mt-16 px-5 md:px-10 lg:px-5 xl:px-0">
        {isFilled.keyText(slice.primary.legal_note) && (
          <p
            className={cn('col-span-full text-[9px] font-[500] leading-[11px] tracking-[0.09px]', {
              'text-moss-white': isDarkBackground,
              'text-grey-300': !isDarkBackground,
            })}
          >
            {slice.primary.legal_note}
          </p>
        )}
      </div>
    </Container>
  )
}

const MediaComponent = ({assetField}: {assetField: HeroV2SliceBentoGridReskinPrimaryBentoGridItem}) => {
  if (isFilled.image(assetField.image)) {
    return (
      <picture>
        {/* Mobile first */}
        <source media="(max-width: 640px)" srcSet={`${assetField.image.Mobile.url}`} sizes="100vw" />
        <PrismicNextImage
          field={assetField.image}
          alt=""
          width={700}
          height={700}
          className="size-full"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          priority
        />
      </picture>
    )
  } else if (isFilled.linkToMedia(assetField.asset)) {
    switch (assetField.asset.kind) {
      case 'image':
        return <Image src={assetField.asset.url} alt="" width={700} height={700} className="size-full" priority />
      default:
        return null
    }
  }

  return <></>
}
